import { c as _c } from "react/compiler-runtime";
import React from "react";
import clsx from "clsx";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { ImageScale } from "@/helpers/image.helpers";
import WaveDropPartContentMediaImage from "./WaveDropPartContentMediaImage";
function isRenderableMedia(mimeType: string, url: string): boolean {
  return mimeType.includes("image") || mimeType.includes("video") || mimeType.includes("audio") || mimeType === "model/gltf-binary" || mimeType === "model/gltf+json" || mimeType === "text/html" || url.endsWith(".glb") || url.endsWith(".gltf");
}
interface WaveDropPartContentMediasProps {
  readonly activePart: ApiDropPart;
  readonly disableMediaInteraction?: boolean | undefined;
  readonly isCompetitionDrop?: boolean | undefined;
  readonly imageScale?: ImageScale | undefined;
  readonly mediaContainerHeightClassName?: string | undefined;
  readonly fullWidthMedia?: boolean | undefined;
}
const WaveDropPartContentMedias: React.FC<WaveDropPartContentMediasProps> = t0 => {
  const $ = _c(18);
  const {
    activePart,
    disableMediaInteraction: t1,
    isCompetitionDrop: t2,
    imageScale: t3,
    mediaContainerHeightClassName: t4,
    fullWidthMedia: t5
  } = t0;
  const disableMediaInteraction = t1 === undefined ? false : t1;
  const isCompetitionDrop = t2 === undefined ? false : t2;
  const imageScale = t3 === undefined ? ImageScale.AUTOx450 : t3;
  const mediaContainerHeightClassName = t4 === undefined ? "tw-h-64" : t4;
  const fullWidthMedia = t5 === undefined ? false : t5;
  if (!activePart.media.length) {
    return null;
  }
  const hasContentBeforeMedia = Boolean(activePart.content?.trim()) || Boolean(activePart.quoted_drop?.drop_id);
  let topSpacingClassName = "tw-mt-1";
  if (hasContentBeforeMedia) {
    topSpacingClassName = "tw-mt-4";
  } else {
    if (fullWidthMedia) {
      topSpacingClassName = "tw-mt-0";
    }
  }
  let t6;
  if ($[0] !== topSpacingClassName) {
    t6 = clsx(topSpacingClassName, "tw-space-y-3");
    $[0] = topSpacingClassName;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const mediaStackClassName = t6;
  let t7;
  if ($[2] !== fullWidthMedia || $[3] !== mediaContainerHeightClassName) {
    t7 = t8 => {
      const {
        groupedImage,
        useIntrinsicHeightMedia,
        useCompactLink
      } = t8;
      if (groupedImage) {
        return "tw-min-w-0 tw-w-full";
      }
      if (useCompactLink) {
        return "tw-w-full";
      }
      if (useIntrinsicHeightMedia) {
        return "tw-flex tw-w-full tw-min-w-[min(200px,100%)] tw-max-h-64 tw-items-center tw-justify-center";
      }
      return clsx("tw-flex tw-min-w-[min(200px,100%)] tw-items-center tw-justify-center", mediaContainerHeightClassName, fullWidthMedia && "tw-w-full");
    };
    $[2] = fullWidthMedia;
    $[3] = mediaContainerHeightClassName;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const getMediaContainerClassName = t7;
  let t8;
  if ($[5] !== disableMediaInteraction || $[6] !== fullWidthMedia || $[7] !== getMediaContainerClassName || $[8] !== imageScale || $[9] !== isCompetitionDrop) {
    t8 = (media, i, t9) => {
      const groupedImage_0 = t9 === undefined ? false : t9;
      const useNaturalHeightImage = fullWidthMedia && media.mime_type.includes("image");
      const useImageIntrinsicHeight = !fullWidthMedia && media.mime_type.includes("image");
      const useVideoIntrinsicHeight = media.mime_type.includes("video");
      const useCompactLink_0 = !isRenderableMedia(media.mime_type, media.url);
      const mediaContainerClassName = getMediaContainerClassName({
        groupedImage: groupedImage_0,
        useIntrinsicHeightMedia: useNaturalHeightImage || useImageIntrinsicHeight || useVideoIntrinsicHeight,
        useCompactLink: useCompactLink_0
      });
      let mediaContent;
      if (disableMediaInteraction) {
        mediaContent = <MediaDisplay media_mime_type={media.mime_type} media_url={media.url} disableMediaInteraction={disableMediaInteraction} imageScale={imageScale} />;
      } else {
        if (useNaturalHeightImage || useImageIntrinsicHeight) {
          mediaContent = <WaveDropPartContentMediaImage src={media.url} imageScale={imageScale} imageObjectPosition={useImageIntrinsicHeight ? "left top" : "center"} />;
        } else {
          mediaContent = <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} isCompetitionDrop={isCompetitionDrop} imageScale={imageScale} />;
        }
      }
      return <div key={`part-${i}-media-${media.url}`}><div className={mediaContainerClassName}>{mediaContent}</div></div>;
    };
    $[5] = disableMediaInteraction;
    $[6] = fullWidthMedia;
    $[7] = getMediaContainerClassName;
    $[8] = imageScale;
    $[9] = isCompetitionDrop;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const renderMedia = t8;
  let mediaElements;
  if ($[11] !== activePart.media || $[12] !== disableMediaInteraction || $[13] !== renderMedia) {
    mediaElements = [];
    let imageRun = [];
    const flushImageRun = () => {
      if (imageRun.length === 0) {
        return;
      }
      if (imageRun.length === 1) {
        const [item] = imageRun;
        if (item) {
          mediaElements.push(renderMedia(item.media, item.index));
        }
        imageRun = [];
        return;
      }
      mediaElements.push(<div key={`image-media-group:${imageRun.map(_temp).join("|")}`} className="tw-grid tw-w-full tw-grid-cols-1 tw-items-start tw-justify-start tw-gap-2 sm:tw-grid-cols-[repeat(auto-fit,minmax(min(12rem,100%),16rem))]">{imageRun.map(item_1 => renderMedia(item_1.media, item_1.index, true))}</div>);
      imageRun = [];
    };
    activePart.media.forEach((media_0, i_0) => {
      if (!disableMediaInteraction && media_0.mime_type.includes("image")) {
        imageRun.push({
          media: media_0,
          index: i_0
        });
        return;
      }
      flushImageRun();
      mediaElements.push(renderMedia(media_0, i_0));
    });
    flushImageRun();
    $[11] = activePart.media;
    $[12] = disableMediaInteraction;
    $[13] = renderMedia;
    $[14] = mediaElements;
  } else {
    mediaElements = $[14];
  }
  let t9;
  if ($[15] !== mediaElements || $[16] !== mediaStackClassName) {
    t9 = <div className={mediaStackClassName}>{mediaElements}</div>;
    $[15] = mediaElements;
    $[16] = mediaStackClassName;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default WaveDropPartContentMedias;
function _temp(item_0) {
  return `${item_0.index}:${item_0.media.url}`;
}